import { useCallback, useEffect, useRef, useState } from 'react';
import { Env } from '../../shared';
import { useAdminApi, ApiError } from '../../shared/adminApi';

// GET /admin/orgs/{orgId} — response matches backend/lambdas/orgs-admin/get-org.

export interface Org {
  orgId: string;
  name: string;
  enterpriseMonthlyCents: number | null;
  pricingState: 'priced' | 'not_yet_priced';
  /** Live Enterprise item exists — re-pricing does not re-bill the running sub. */
  orgTierActive: boolean;
  createdAt: string | null;
}

export type OrgLookupStatus = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

interface UseGetOrgResult {
  org: Org | null;
  status: OrgLookupStatus;
  error: string | null;
  /** Reflect a just-saved amount without a refetch (price-setter is the only writer). */
  applyPrice: (cents: number | null) => void;
}

/**
 * Auto-fetches the org whenever a valid id + env is supplied. Pass an empty /
 * invalid id (the caller validates the UUID) to reset to idle without a call.
 */
export function useGetOrg(env: Env, orgId: string, enabled: boolean): UseGetOrgResult {
  const { request } = useAdminApi();
  const [org, setOrg] = useState<Org | null>(null);
  const [status, setStatus] = useState<OrgLookupStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const requestRef = useRef(request);
  requestRef.current = request;

  useEffect(() => {
    if (!enabled) {
      setOrg(null);
      setStatus('idle');
      setError(null);
      return;
    }

    let isMounted = true;
    setStatus('loading');
    setError(null);
    requestRef
      .current<Org>({ env, path: `/admin/orgs/${orgId}` })
      .then(data => {
        if (!isMounted) return;
        setOrg(data);
        setStatus('found');
      })
      .catch((err: unknown) => {
        if (!isMounted) return;
        setOrg(null);
        if (err instanceof ApiError && err.status === 404) {
          setStatus('not_found');
        } else {
          setStatus('error');
          setError(err instanceof Error ? err.message : 'Failed to load organization');
        }
      });
    return () => {
      isMounted = false;
    };
  }, [env, orgId, enabled]);

  const applyPrice = useCallback((cents: number | null) => {
    setOrg(prev =>
      prev
        ? {
            ...prev,
            enterpriseMonthlyCents: cents,
            pricingState: cents === null ? 'not_yet_priced' : 'priced',
          }
        : prev,
    );
  }, []);

  return { org, status, error, applyPrice };
}
